<script>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useTrack, useAlert } from 'dashboard/composables';
import { vOnClickOutside } from '@vueuse/components';
import { REPLY_EDITOR_MODES, CHAR_LENGTH_WARNING } from './constants';
import { CAPTAIN_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import EditorModeToggle from './EditorModeToggle.vue';
import CopilotMenuBar from './CopilotMenuBar.vue';

export default {
  name: 'ReplyTopPanel',
  components: {
    NextButton,
    Spinner,
    EditorModeToggle,
    CopilotMenuBar,
  },
  directives: {
    OnClickOutside: vOnClickOutside,
  },
  props: {
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    isReplyRestricted: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
    hasContent: {
      type: Boolean,
      default: false,
    },
    editorContent: {
      type: String,
      default: '',
    },
    charactersRemaining: {
      type: Number,
      default: 0,
    },
    isMessageLengthReachingThreshold: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      default: null,
    },
  },
  emits: ['setReplyMode', 'toggleEditorSize', 'executeCopilotAction'],
  setup(props, { emit }) {
    const store = useStore();
    const isClearingWaiting = ref(false);
    const isDisqualifying = ref(false);
    const isMovingStage = ref(false);
    const showStageDropdown = ref(false);
    const stageDropdownRef = ref(null);

    const currentChat = computed(() => store.getters.getSelectedChat);
    const isWaiting = computed(() => {
      return Boolean(currentChat.value?.waiting_since);
    });

    // Custom Attribute Funnel Definition
    const funnelAttribute = computed(() => {
      const allAttrs = store.getters['attributes/getAttributesByModel']('conversation_attribute') || [];
      return allAttrs.find(a => a.attribute_key === 'servidor') || allAttrs.find(a => a.attribute_display_type === 'list') || null;
    });

    const funnelStages = computed(() => {
      if (funnelAttribute.value && funnelAttribute.value.attribute_values && funnelAttribute.value.attribute_values.length > 0) {
        return funnelAttribute.value.attribute_values;
      }
      return [
        'Recepção e Qualificação',
        'Holerite',
        'Proposta',
        'Autorizado',
        'Aguardando Assinatura',
        'Analise Viabiliadade',
        'Pasta Fechada',
        'Distribuido',
        'PRAZOS',
        'Com Pedido',
        'Pronto para Cumprir',
      ];
    });

    const defaultPalette = {
      'Recepção e Qualificação': '#3b82f6',
      'Holerite': '#06b6d4',
      'Proposta': '#f59e0b',
      'Autorizado': '#8b5cf6',
      'Aguardando Assinatura': '#ec4899',
      'Analise Viabiliadade': '#6366f1',
      'Pasta Fechada': '#10b981',
      'Distribuido': '#14b8a6',
      'PRAZOS': '#ef4444',
      'Prazos Doc Enviados': '#f97316',
      'Com Pedido': '#84cc16',
      'Pronto para Cumprir': '#10b981',
    };

    const getStageColor = stageName => {
      if (!stageName) return '#64748b';
      if (funnelAttribute.value && funnelAttribute.value.attribute_description) {
        try {
          const config = JSON.parse(funnelAttribute.value.attribute_description);
          if (config && config.colors && config.colors[stageName]) {
            return config.colors[stageName];
          }
        } catch (e) {
          // ignore
        }
      }
      return defaultPalette[stageName] || '#64748b';
    };

    const getContrastYIQ = hexcolor => {
      if (!hexcolor || typeof hexcolor !== 'string' || !hexcolor.startsWith('#')) return '#ffffff';
      const cleanHex = hexcolor.replace('#', '');
      const r = parseInt(cleanHex.substr(0, 2), 16) || 0;
      const g = parseInt(cleanHex.substr(2, 2), 16) || 0;
      const b = parseInt(cleanHex.substr(4, 2), 16) || 0;
      const yiq = (r * 299 + g * 587 + b * 114) / 1000;
      return yiq >= 150 ? '#0f172a' : '#ffffff';
    };

    const currentStage = computed(() => {
      const customAttrs = currentChat.value?.custom_attributes || {};
      const attrKey = funnelAttribute.value?.attribute_key || 'servidor';
      return customAttrs[attrKey] || '';
    });

    const currentStageIndex = computed(() => {
      if (!currentStage.value) return -1;
      const stageNorm = String(currentStage.value).toLowerCase().trim();
      return funnelStages.value.findIndex(s => String(s).toLowerCase().trim() === stageNorm);
    });

    const prevStage = computed(() => {
      const idx = currentStageIndex.value;
      if (idx > 0) {
        return funnelStages.value[idx - 1];
      }
      return null;
    });

    const nextStage = computed(() => {
      const idx = currentStageIndex.value;
      if (idx >= 0 && idx < funnelStages.value.length - 1) {
        return funnelStages.value[idx + 1];
      }
      if (idx === -1 && funnelStages.value.length > 0) {
        return funnelStages.value[0];
      }
      return null;
    });

    const handleMoveToStage = async targetStage => {
      const targetId = props.conversationId || currentChat.value?.id;
      if (!targetId || !targetStage || isMovingStage.value) return;

      const attrKey = funnelAttribute.value?.attribute_key || 'servidor';
      isMovingStage.value = true;
      showStageDropdown.value = false;

      try {
        await store.dispatch('updateCustomAttributes', {
          conversationId: targetId,
          customAttributes: {
            [attrKey]: targetStage,
          },
        });
        useAlert(`Etapa alterada para: ${targetStage}`);
      } catch (err) {
        useAlert('Erro ao alterar etapa do Kanban: ' + (err?.message || 'Falha na requisição'));
      } finally {
        isMovingStage.value = false;
      }
    };

    const toggleStageDropdown = () => {
      showStageDropdown.value = !showStageDropdown.value;
    };

    const closeStageDropdown = () => {
      showStageDropdown.value = false;
    };

    const handleMarkAsAttended = async () => {
      const targetId = props.conversationId || currentChat.value?.id;
      if (!targetId || isClearingWaiting.value) return;

      isClearingWaiting.value = true;
      try {
        await store.dispatch('clearWaitingStatus', targetId);
        useAlert('Conversa marcada como atendida com sucesso!');
      } catch (err) {
        useAlert('Erro ao marcar conversa como atendida');
      } finally {
        isClearingWaiting.value = false;
      }
    };

    const handleDisqualify = async () => {
      const targetId = props.conversationId || currentChat.value?.id;
      if (!targetId || isDisqualifying.value) return;

      const confirmMsg = 'Deseja realmente desqualificar este cliente?\n\n• Marcará como RESOLVIDO\n• Removerá do Kanban\n• Adicionará tag "desqualificado"\n• Removerá equipe e atendente\n• Gerará nota interna com o seu nome de operador';
      if (!window.confirm(confirmMsg)) return;

      isDisqualifying.value = true;
      try {
        await store.dispatch('disqualifyConversation', targetId);
        useAlert('Cliente desqualificado e marcado como RESOLVIDO com sucesso!');
      } catch (err) {
        useAlert('Erro ao desqualificar cliente: ' + (err?.message || 'Falha na requisição'));
      } finally {
        isDisqualifying.value = false;
      }
    };

    const setReplyMode = mode => {
      emit('setReplyMode', mode);
    };
    const handleReplyClick = () => {
      if (props.isReplyRestricted) return;
      setReplyMode(REPLY_EDITOR_MODES.REPLY);
    };
    const handleNoteClick = () => {
      setReplyMode(REPLY_EDITOR_MODES.NOTE);
    };
    const handleModeToggle = () => {
      const newMode =
        props.mode === REPLY_EDITOR_MODES.REPLY
          ? REPLY_EDITOR_MODES.NOTE
          : REPLY_EDITOR_MODES.REPLY;
      setReplyMode(newMode);
    };

    const { captainTasksEnabled } = useCaptain();
    const showCopilotMenu = ref(false);
    const copilotToggleRef = ref(null);

    const handleCopilotAction = (actionKey, data) => {
      emit('executeCopilotAction', actionKey, data || props.editorContent);
      showCopilotMenu.value = false;
    };

    const toggleCopilotMenu = () => {
      const isOpening = !showCopilotMenu.value;
      if (isOpening) {
        useTrack(CAPTAIN_EVENTS.EDITOR_AI_MENU_OPENED, {
          conversationId: props.conversationId,
          entryPoint: 'top_panel',
        });
      }
      showCopilotMenu.value = isOpening;
    };

    const handleClickOutside = () => {
      showCopilotMenu.value = false;
    };

    const keyboardEvents = {
      'Alt+KeyP': {
        action: () => handleNoteClick(),
        allowOnFocusedInput: false,
      },
      'Alt+KeyL': {
        action: () => handleReplyClick(),
        allowOnFocusedInput: false,
      },
    };
    useKeyboardEvents(keyboardEvents);

    return {
      handleModeToggle,
      handleReplyClick,
      handleNoteClick,
      handleMarkAsAttended,
      handleDisqualify,
      handleMoveToStage,
      toggleStageDropdown,
      closeStageDropdown,
      showStageDropdown,
      stageDropdownRef,
      currentStage,
      prevStage,
      nextStage,
      funnelStages,
      getStageColor,
      getContrastYIQ,
      isWaiting,
      isClearingWaiting,
      isDisqualifying,
      isMovingStage,
      REPLY_EDITOR_MODES,
      captainTasksEnabled,
      handleCopilotAction,
      showCopilotMenu,
      copilotToggleRef,
      toggleCopilotMenu,
      handleClickOutside,
    };
  },
  computed: {
    replyButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.REPLY,
      };
    },
    noteButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.NOTE,
      };
    },
    charLengthClass() {
      return this.charactersRemaining < 0 ? 'text-n-ruby-9' : 'text-n-slate-11';
    },
    characterLengthWarning() {
      return this.charactersRemaining < 0
        ? `${-this.charactersRemaining} ${CHAR_LENGTH_WARNING.NEGATIVE}`
        : `${this.charactersRemaining} ${CHAR_LENGTH_WARNING.UNDER_50}`;
    },
  },
};
</script>

<template>
  <div
    class="flex justify-between gap-1.5 sm:gap-2 h-[3.25rem] items-center ltr:pl-2.5 sm:ltr:pl-3 ltr:pr-2 rtl:pr-2.5 sm:rtl:pr-3 rtl:pl-2 w-full relative z-20"
  >
    <!-- Botões da esquerda com scroll horizontal no-scrollbar -->
    <div class="flex items-center gap-1.5 sm:gap-2 overflow-x-auto no-scrollbar py-1 min-w-0 flex-1">
      <EditorModeToggle
        :mode="mode"
        :disabled="disabled"
        :is-reply-restricted="isReplyRestricted"
        @toggle-mode="handleModeToggle"
      />
      
      <!-- Botão Marcar como Atendido -->
      <button
        type="button"
        class="flex items-center gap-1 sm:gap-1.5 h-7 sm:h-8 px-2 sm:px-3 rounded-full text-[11px] sm:text-xs font-medium transition-all duration-200 cursor-pointer border select-none active:scale-95 shadow-sm flex-shrink-0"
        :class="isWaiting
          ? 'bg-blue-50 dark:bg-blue-950/40 text-[#2B8EFF] dark:text-[#52a5ff] border-blue-200 dark:border-blue-800/60 hover:bg-blue-100 dark:hover:bg-blue-900/60'
          : 'bg-n-alpha-2 text-n-slate-11 border-n-slate-4 hover:text-n-slate-12 hover:bg-n-alpha-3'"
        :title="'Marcar como Atendido (limpar pendência de resposta e remover de Não Atendidas)'"
        :disabled="isClearingWaiting || disabled"
        @click="handleMarkAsAttended"
      >
        <Spinner v-if="isClearingWaiting" size="xs" color-scheme="primary" />
        <span v-else class="i-lucide-check-check size-3 sm:size-3.5 text-[#2B8EFF] flex-shrink-0" />
        <span class="whitespace-nowrap">
          <span class="hidden sm:inline">Marcar como </span>Atendido
        </span>
      </button>

      <!-- Botão Desqualificado -->
      <button
        type="button"
        class="flex items-center gap-1 sm:gap-1.5 h-7 sm:h-8 px-2 sm:px-3 rounded-full text-[11px] sm:text-xs font-medium transition-all duration-200 cursor-pointer border select-none active:scale-95 shadow-sm bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 border-red-200 dark:border-red-900/60 hover:bg-red-100 dark:hover:bg-red-900/60 flex-shrink-0"
        :title="'Desqualificar cliente (marcar como RESOLVIDO, remover do Kanban, desatribuir agente/time, aplicar tag desqualificado e registrar nota interna)'"
        :disabled="isDisqualifying || disabled"
        @click="handleDisqualify"
      >
        <Spinner v-if="isDisqualifying" size="xs" color-scheme="alert" />
        <span v-else class="i-lucide-user-x size-3 sm:size-3.5 text-red-600 dark:text-red-400 flex-shrink-0" />
        <span class="whitespace-nowrap font-medium">Desqualificado</span>
      </button>

      <!-- SEÇÃO KANBAN STEPPER UNIFICADO: [ ◄ | ETAPA ATUAL ▾ | ► ] -->
      <div class="flex items-center flex-shrink-0 pl-1 border-l border-slate-200 dark:border-slate-700/60 relative">
        <div class="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800/90 border border-slate-200/90 dark:border-slate-700/80 p-0.5 shadow-xs">
          <!-- 1. BOTÃO VOLTAR (◄) -->
          <button
            type="button"
            class="flex items-center justify-center size-7 sm:size-7.5 rounded-full transition-all duration-150 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-700 active:scale-95 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed cursor-pointer flex-shrink-0"
            :title="prevStage ? `Voltar etapa para: ${prevStage}` : 'Primeira etapa do funil'"
            :disabled="!prevStage || isMovingStage || disabled"
            @click="handleMoveToStage(prevStage)"
          >
            <span class="i-lucide-chevron-left size-4 flex-shrink-0" />
          </button>

          <!-- 2. ETAPA ATUAL (CENTRO COM BADGE COLORIDO + DROPDOWN) -->
          <div class="relative">
            <button
              type="button"
              class="flex items-center gap-1.5 h-7 sm:h-7.5 px-2.5 sm:px-3 rounded-full text-xs font-semibold transition-all duration-150 cursor-pointer select-none active:scale-95 hover:bg-white dark:hover:bg-slate-700/90 text-slate-800 dark:text-slate-100 flex-shrink-0 shadow-2xs"
              :title="'Etapa atual no Kanban (clique para escolher qualquer etapa)'"
              :disabled="isMovingStage || disabled"
              @click="toggleStageDropdown"
            >
              <!-- Dot com a cor da etapa atual -->
              <span
                class="size-2.5 rounded-full flex-shrink-0 shadow-xs ring-1 ring-black/10 dark:ring-white/20"
                :style="{ backgroundColor: currentStage ? getStageColor(currentStage) : '#64748b' }"
              />
              
              <!-- Spinner durante movimentação -->
              <Spinner v-if="isMovingStage" size="xs" color-scheme="primary" />

              <!-- Nome da Etapa Atual (sem corte, visível e destacado) -->
              <span v-else class="whitespace-nowrap font-bold text-[11px] sm:text-xs tracking-tight">
                {{ currentStage || 'Sem Etapa' }}
              </span>

              <!-- Chevron para baixo indicando que abre menu -->
              <span class="i-lucide-chevron-down size-3 text-slate-400 dark:text-slate-500 opacity-80 flex-shrink-0" />
            </button>

            <!-- Dropdown com todas as 11 etapas do funil -->
            <div
              v-if="showStageDropdown"
              v-on-click-outside="closeStageDropdown"
              class="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/80 rounded-xl shadow-2xl p-1.5 min-w-[220px] max-h-[320px] overflow-y-auto"
            >
              <div class="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 border-b border-slate-100 dark:border-slate-800 mb-1 flex items-center justify-between">
                <span>Mudar Etapa do Kanban</span>
                <span class="text-[9px] lowercase font-normal text-slate-400">1 clique</span>
              </div>
              <button
                v-for="stg in funnelStages"
                :key="stg"
                type="button"
                class="w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-left transition-all duration-150 hover:bg-slate-100 dark:hover:bg-slate-800/80 mb-0.5 cursor-pointer"
                :class="{ 'ring-1 ring-primary-500/50 bg-primary-50/50 dark:bg-primary-950/20': currentStage === stg }"
                @click="handleMoveToStage(stg)"
              >
                <div class="flex items-center gap-2 truncate">
                  <span
                    class="size-2.5 rounded-full flex-shrink-0 shadow-xs"
                    :style="{ backgroundColor: getStageColor(stg) }"
                  />
                  <span class="truncate text-slate-700 dark:text-slate-200">
                    {{ stg }}
                  </span>
                </div>
                <span
                  v-if="currentStage === stg"
                  class="i-lucide-check size-3.5 text-primary-500 flex-shrink-0"
                />
              </button>
            </div>
          </div>

          <!-- 3. BOTÃO AVANÇAR (►) -->
          <button
            type="button"
            class="flex items-center justify-center size-7 sm:size-7.5 rounded-full transition-all duration-150 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-700 active:scale-95 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed cursor-pointer flex-shrink-0"
            :title="nextStage ? `Avançar etapa para: ${nextStage}` : 'Última etapa do funil'"
            :disabled="!nextStage || isMovingStage || disabled"
            @click="handleMoveToStage(nextStage)"
          >
            <span class="i-lucide-chevron-right size-4 flex-shrink-0" />
          </button>
        </div>
      </div>
    </div>

    <!-- Indicador de limite de caracteres -->
    <div class="hidden md:flex items-center mx-2 my-0 flex-shrink-0">
      <div v-if="isMessageLengthReachingThreshold" class="text-xs">
        <span :class="charLengthClass">
          {{ characterLengthWarning }}
        </span>
      </div>
    </div>

    <!-- Seção do Copilot (Estrelinha) e Maximizar com z-index elevado e visibilidade total -->
    <div v-if="captainTasksEnabled" class="flex items-center gap-1 sm:gap-2 flex-shrink-0 relative z-30">
      <div class="relative">
        <NextButton
          ref="copilotToggleRef"
          ghost
          :disabled="disabled || isEditorDisabled"
          :class="{
            'text-n-violet-9 hover:enabled:!bg-n-violet-3': !showCopilotMenu,
            'text-n-violet-9 bg-n-violet-3': showCopilotMenu,
          }"
          sm
          icon="i-ph-sparkle-fill"
          @click="toggleCopilotMenu"
        />
        <CopilotMenuBar
          v-if="showCopilotMenu"
          v-on-click-outside="[
            handleClickOutside,
            { ignore: [copilotToggleRef] },
          ]"
          :has-selection="false"
          :has-content="hasContent"
          :conversation-id="conversationId"
          class="ltr:right-0 rtl:left-0 bottom-full mb-2 z-50 absolute shadow-2xl"
          @execute-copilot-action="handleCopilotAction"
        />
      </div>
      <NextButton
        ghost
        class="text-n-slate-11"
        sm
        icon="i-lucide-maximize-2"
        @click="$emit('toggleEditorSize')"
      />
    </div>
  </div>
</template>
